import React from 'react'

import {
  Body1,
  Box,
  Color,
  Display,
  Divider,
  FlexDirection,
  FontWeight,
  Heading4,
  Heading5,
  JustifyContent,
  Space,
} from '@gousto-internal/citrus-react'
import Immutable from 'immutable'
import moment from 'moment'

import { RecipeListItem } from './RecipeListItem'

type OrderDetailsProps = {
  deliveryDate: string
  numRecipes: number
  recipeItems: any
  recipes: any
  productTotal: number
  line1: string
  town: string
  postcode: string
  orderNumber: string
  percentgeOff: number
  recipeTotal: number
  totalDiscount: number
  deliveryTotal: number
  surchargeTotal: number
  surchargeCount: number
  total: number
}

export const OrderDetails = ({
  numRecipes,
  recipeItems,
  recipes,
  orderNumber,
  percentgeOff,
  deliveryDate,
  productTotal,
  line1,
  town,
  postcode,
  recipeTotal,
  totalDiscount,
  deliveryTotal,
  surchargeTotal,
  surchargeCount,
  total,
}: OrderDetailsProps) => {
  const discountPercent = Math.trunc(percentgeOff) || 0
  const showDiscount = discountPercent !== 0 && totalDiscount > 0
  const formattedDeliveryDate = moment(
    deliveryDate,
    ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD'],
    true,
  ).format('dddd Do MMMM')

  const recipesDetails: Array<{
    orderItemId: string
    title: string
    numPortions: number
    imageUrl?: string
  }> = []

  recipeItems?.forEach((recipeItem: any) => {
    const recipeId = recipeItem.get('itemableId')
    const recipeQty = recipeItem.get('quantity')
    const recipe = recipes?.get(recipeId, Immutable.Map())

    if (recipe?.has('title') && recipe?.has('media')) {
      const imageUrl =
        recipe.getIn(['media', 'images', 0, 'urls', 0, 'src']) ||
        recipe.getIn(['media', 'images', 0, 'urls', 0, 'url']) ||
        ''

      recipesDetails.push({
        orderItemId: recipeId,
        title: recipe.get('title'),
        numPortions: parseFloat(recipeQty),
        imageUrl,
      })
    }
  })

  return (
    <>
      <Space size={[0, 2]} />
      <Box>
        {recipesDetails.map((recipe) => (
          <Box
            key={recipe.orderItemId}
            display={Display.Flex}
            flexDirection={FlexDirection.Column}
            gap={2}
          >
            <RecipeListItem
              key={recipe.orderItemId}
              imageUrl={recipe.imageUrl}
              title={recipe.title}
              servings={recipe.numPortions}
            />
          </Box>
        ))}
        <Space size={[3, 3]} />
        <Heading4 size={5}>Order Summary</Heading4>
        <Space size={4} />
        <Box display={Display.Flex} flexDirection={FlexDirection.Column} gap={3}>
          <Box
            display={Display.Flex}
            flexDirection={FlexDirection.Row}
            justifyContent={JustifyContent.SpaceBetween}
          >
            <Body1 size={4}>Recipes ({numRecipes})</Body1>
            <Body1 size={4}>£{recipeTotal}</Body1>
          </Box>
          {showDiscount ? (
            <Box
              display={Display.Flex}
              flexDirection={FlexDirection.Row}
              justifyContent={JustifyContent.SpaceBetween}
            >
              <Body1 size={4} fontWeight={FontWeight.Bold} color={Color.Success_900}>
                Discount (-{discountPercent}%)
              </Body1>
              <Body1 size={4} fontWeight={FontWeight.Bold} color={Color.Success_900}>
                £{totalDiscount}
              </Body1>
            </Box>
          ) : null}
          {productTotal > 0 ? (
            <Box
              display={Display.Flex}
              flexDirection={FlexDirection.Row}
              justifyContent={JustifyContent.SpaceBetween}
            >
              <Body1 size={4}>Sides and market items</Body1>
              <Body1 size={4}>£{productTotal}</Body1>
            </Box>
          ) : null}
          {surchargeCount > 0 && surchargeTotal > 0 ? (
            <Box
              display={Display.Flex}
              flexDirection={FlexDirection.Row}
              justifyContent={JustifyContent.SpaceBetween}
            >
              <Body1 size={4}>Surcharges and/or savings</Body1>
              <Body1 size={4}>£{surchargeTotal}</Body1>
            </Box>
          ) : null}
          <Box
            display={Display.Flex}
            flexDirection={FlexDirection.Row}
            justifyContent={JustifyContent.SpaceBetween}
          >
            <Body1 size={4}>Delivery cost</Body1>
            <Body1 size={4}>£{deliveryTotal}</Body1>
          </Box>
          <Box
            display={Display.Flex}
            flexDirection={FlexDirection.Row}
            justifyContent={JustifyContent.SpaceBetween}
          >
            <Body1 size={4} fontWeight={FontWeight.Bold} color={Color.ColdGrey_800}>
              Total
            </Body1>
            <Body1 size={4} fontWeight={FontWeight.Bold} color={Color.ColdGrey_800}>
              £{total}
            </Body1>
          </Box>
        </Box>
        <Space size={3} />
        <Divider />
        <Space size={3} />
        <Heading4 size={5}>Delivery details</Heading4>
        <Space size={4} />
        <Heading5 size={3}>Delivery date</Heading5>
        <Space size={1} />
        <Body1 size={4}>{formattedDeliveryDate}</Body1>
        <Space size={4} />
        <Heading5 size={3}>Delivery address</Heading5>
        <Space size={1} />
        <Body1 size={4}>{line1}</Body1>
        <Body1 size={4}>{town}</Body1>
        <Body1 size={4}>{postcode}</Body1>
        <Space size={3} />
        <Divider />
        <Space size={3} />
        <Box
          display={Display.Flex}
          flexDirection={FlexDirection.Row}
          justifyContent={JustifyContent.SpaceBetween}
        >
          <Body1 size={4}>Order number</Body1>
          <Body1 size={4}>{orderNumber}</Body1>
        </Box>
      </Box>
    </>
  )
}
